import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface ProfileBuilderProps {
  sessionId: string;
  entryIntent: { primary_vertical: string; source: string; confidence: number };
  onComplete: (result: { vertical: any; phase: number; session_value: number }) => void;
  onProfileData?: (profile: Record<string, unknown>) => void;
  onStepChange?: (step: number) => void;
  onError?: (error: string) => void;
}

type CreditTier = 'excellent' | 'good' | 'fair' | 'poor';
type DebtRange = 'over_10k' | 'under_10k' | 'none';
type AgeRange = 'under_25' | '25_34' | '35_44' | '45_54' | '55_64' | '65_plus';
type EmploymentStatus = 'employed' | 'self_employed' | 'retired' | 'unemployed';
type InsuranceStatus = 'insured' | 'uninsured';
type IncomeBand = 'under_30k' | '30k_60k' | '60k_100k' | 'over_100k';

interface CollectedProfile {
  homeowner?: boolean;
  age_range?: AgeRange;
  credit_tier?: CreditTier;
  employment_status?: EmploymentStatus;
  income_band?: IncomeBand;
  debt_flag?: boolean;
  debt_range?: DebtRange;
  insurance_status?: InsuranceStatus;
  state?: string;
}

/* ------------------------------------------------------------------ */
/*  US States                                                          */
/* ------------------------------------------------------------------ */

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA',
  'HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
] as const;

const STATE_NAMES: Record<string, string> = {
  AL:'Alabama',AK:'Alaska',AZ:'Arizona',AR:'Arkansas',CA:'California',
  CO:'Colorado',CT:'Connecticut',DE:'Delaware',FL:'Florida',GA:'Georgia',
  HI:'Hawaii',ID:'Idaho',IL:'Illinois',IN:'Indiana',IA:'Iowa',
  KS:'Kansas',KY:'Kentucky',LA:'Louisiana',ME:'Maine',MD:'Maryland',
  MA:'Massachusetts',MI:'Michigan',MN:'Minnesota',MS:'Mississippi',MO:'Missouri',
  MT:'Montana',NE:'Nebraska',NV:'Nevada',NH:'New Hampshire',NJ:'New Jersey',
  NM:'New Mexico',NY:'New York',NC:'North Carolina',ND:'North Dakota',OH:'Ohio',
  OK:'Oklahoma',OR:'Oregon',PA:'Pennsylvania',RI:'Rhode Island',SC:'South Carolina',
  SD:'South Dakota',TN:'Tennessee',TX:'Texas',UT:'Utah',VT:'Vermont',
  VA:'Virginia',WA:'Washington',WV:'West Virginia',WI:'Wisconsin',WY:'Wyoming',
};

/* ------------------------------------------------------------------ */
/*  Animation variants                                                 */
/* ------------------------------------------------------------------ */

const slideVariants = {
  enter:  { x: 80, opacity: 0 },
  center: { x: 0,  opacity: 1 },
  exit:   { x: -80, opacity: 0 },
};

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function ProfileBuilder({
  sessionId,
  entryIntent,
  onComplete,
  onProfileData,
  onStepChange,
  onError,
}: ProfileBuilderProps) {
  const [step, setStep] = useState(0);           // 0-6 = questions, 7 = loading
  const [profile, setProfile] = useState<CollectedProfile>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedState, setSelectedState] = useState('');
  const [stateError, setStateError] = useState<string | null>(null);

  const totalSteps = 8;

  /* ---- API call -------------------------------------------------- */

  const callScoringAPI = useCallback(
    async (finalProfile: CollectedProfile) => {
      setLoading(true);
      setError(null);

      const SUPABASE_URL =
        import.meta.env?.VITE_SUPABASE_URL ||
        'https://khwvlnkrtrwvfbnnjsrc.supabase.co';
      const SUPABASE_KEY = import.meta.env?.VITE_SUPABASE_ANON_KEY || '';

      try {
        const res = await fetch(
          `${SUPABASE_URL}/functions/v1/scoring-next`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              apikey: SUPABASE_KEY,
              Authorization: `Bearer ${SUPABASE_KEY}`,
            },
            body: JSON.stringify({
              session_id: sessionId,
              action: 'start',
              entry_intent: entryIntent,
              profile: finalProfile,
            }),
          },
        );

        if (!res.ok) {
          const text = await res.text();
          throw new Error(text || `HTTP ${res.status}`);
        }

        const data = await res.json();
        onComplete(data);
      } catch (err: any) {
        const msg = err?.message || 'Scoring request failed';
        setError(msg);
        onError?.(msg);
      } finally {
        setLoading(false);
      }
    },
    [sessionId, entryIntent, onComplete, onError],
  );

  /* ---- Advance helpers ------------------------------------------- */

  const advance = useCallback(
    (patch: Partial<CollectedProfile>) => {
      const next = { ...profile, ...patch };
      setProfile(next);

      if (step < totalSteps - 1) {
        const nextStep = step + 1;
        setStep(nextStep);
        onStepChange?.(nextStep);
      } else {
        // Last question answered -- pass profile data up, then fire API
        setStep(totalSteps);
        onProfileData?.(next as Record<string, unknown>);
        callScoringAPI(next);
      }
    },
    [profile, step, callScoringAPI, onProfileData, onStepChange],
  );

  /* ---- Go back to previous step ---------------------------------- */

  const goBack = useCallback(() => {
    if (step > 0) {
      const prevStep = step - 1;
      setStep(prevStep);
      onStepChange?.(prevStep);
    }
  }, [step, onStepChange]);

  /* ---- Reusable option button ------------------------------------ */

  const OptionButton = ({
    label,
    onClick,
  }: {
    label: string;
    onClick: () => void;
  }) => (
    <button
      type="button"
      onClick={onClick}
      className="w-full rounded-xl border border-slate-700 bg-slate-800 px-4 sm:px-6 py-4 sm:py-5 text-left text-sm sm:text-lg font-medium text-white transition-all hover:border-emerald-500 hover:bg-slate-700 active:scale-[0.98]"
    >
      {label}
    </button>
  );

  /* ---- Question renderers ---------------------------------------- */

  const renderQuestion = () => {
    switch (step) {
      /* Q1 — Homeowner --------------------------------------------- */
      case 0:
        return (
          <QuestionWrapper title="Do you own or rent your home?">
            <div className="flex flex-col gap-3">
              <OptionButton label="I own"  onClick={() => advance({ homeowner: true })} />
              <OptionButton label="I rent" onClick={() => advance({ homeowner: false })} />
            </div>
          </QuestionWrapper>
        );

      /* Q2 — Age range --------------------------------------------- */
      case 1:
        return (
          <QuestionWrapper title="What's your age range?" onBack={goBack}>
            <div className="flex flex-col gap-3">
              {([
                ['18-24',  'under_25'],
                ['25-34',  '25_34'],
                ['35-44',  '35_44'],
                ['45-54',  '45_54'],
                ['55-64',  '55_64'],
                ['65+',    '65_plus'],
              ] as [string, AgeRange][]).map(([label, value]) => (
                <OptionButton
                  key={value}
                  label={label}
                  onClick={() => advance({ age_range: value })}
                />
              ))}
            </div>
          </QuestionWrapper>
        );

      /* Q3 — Credit ------------------------------------------------ */
      case 2:
        return (
          <QuestionWrapper title="What's your credit like?" onBack={goBack}>
            <div className="flex flex-col gap-3">
              {([
                ['Excellent (720+)', 'excellent'],
                ['Good (660\u2013719)',    'good'],
                ['Fair (600\u2013659)',    'fair'],
                ['Poor (below 600)',  'poor'],
              ] as [string, CreditTier][]).map(([label, tier]) => (
                <OptionButton
                  key={tier}
                  label={label}
                  onClick={() => advance({ credit_tier: tier })}
                />
              ))}
            </div>
          </QuestionWrapper>
        );

      /* Q4 — Employment status ------------------------------------- */
      case 3:
        return (
          <QuestionWrapper title="What's your employment status?" onBack={goBack}>
            <div className="flex flex-col gap-3">
              {([
                ['Employed',              'employed'],
                ['Self-employed',         'self_employed'],
                ['Retired',               'retired'],
                ['Not currently working', 'unemployed'],
              ] as [string, EmploymentStatus][]).map(([label, value]) => (
                <OptionButton
                  key={value}
                  label={label}
                  onClick={() => advance({ employment_status: value })}
                />
              ))}
            </div>
          </QuestionWrapper>
        );

      /* Q5 — Income range ------------------------------------------ */
      case 4:
        return (
          <QuestionWrapper title="What's your approximate annual income?" onBack={goBack}>
            <div className="flex flex-col gap-3">
              {([
                ['Under $30,000',    'under_30k'],
                ['$30,000 – $60,000', '30k_60k'],
                ['$60,000 – $100,000', '60k_100k'],
                ['Over $100,000',    'over_100k'],
              ] as [string, IncomeBand][]).map(([label, value]) => (
                <OptionButton
                  key={value}
                  label={label}
                  onClick={() => advance({ income_band: value })}
                />
              ))}
            </div>
          </QuestionWrapper>
        );

      /* Q6 — Debt -------------------------------------------------- */
      case 5:
        return (
          <QuestionWrapper title="Do you have any debt you'd like help with?" onBack={goBack}>
            <div className="flex flex-col gap-3">
              <OptionButton
                label="Yes, over $10k"
                onClick={() => advance({ debt_flag: true, debt_range: 'over_10k' })}
              />
              <OptionButton
                label="Yes, under $10k"
                onClick={() => advance({ debt_flag: true, debt_range: 'under_10k' })}
              />
              <OptionButton
                label="No debt"
                onClick={() => advance({ debt_flag: false, debt_range: 'none' })}
              />
            </div>
          </QuestionWrapper>
        );

      /* Q7 — Insurance status -------------------------------------- */
      case 6:
        return (
          <QuestionWrapper title="Do you currently have auto insurance?" onBack={goBack}>
            <div className="flex flex-col gap-3">
              <OptionButton
                label="Yes, I'm insured"
                onClick={() => advance({ insurance_status: 'insured' })}
              />
              <OptionButton
                label="No, I'm uninsured"
                onClick={() => advance({ insurance_status: 'uninsured' })}
              />
            </div>
          </QuestionWrapper>
        );

      /* Q8 — State (always last) ----------------------------------- */
      case 7:
        return (
          <QuestionWrapper title="What state do you live in?" onBack={goBack}>
            <select
              autoFocus
              value={selectedState}
              onChange={(e) => {
                setSelectedState(e.target.value);
                setStateError(null);
              }}
              className={`w-full appearance-none rounded-xl border ${stateError ? 'border-red-500' : 'border-slate-700'} bg-slate-800 px-4 sm:px-5 py-3 sm:py-4 text-sm sm:text-lg text-white outline-none transition-colors focus:border-emerald-500`}
            >
              <option value="" disabled>
                Select your state...
              </option>
              {US_STATES.map((code) => (
                <option key={code} value={code}>
                  {STATE_NAMES[code]} ({code})
                </option>
              ))}
            </select>
            {stateError && (
              <p className="text-sm text-red-400 mt-1">{stateError}</p>
            )}
            <button
              type="button"
              onClick={() => {
                if (!selectedState) {
                  setStateError('Please select your state to continue');
                  return;
                }
                advance({ state: selectedState });
              }}
              className="w-full mt-3 rounded-xl bg-emerald-500 px-4 sm:px-6 py-3 sm:py-4 text-sm sm:text-lg font-semibold text-white transition-colors hover:bg-emerald-600 active:scale-[0.98]"
            >
              Continue
            </button>
          </QuestionWrapper>
        );

      default:
        return null;
    }
  };

  /* ---- Loading / error states ------------------------------------ */

  if (step === totalSteps || loading) {
    return (
      <Shell progress={1}>
        {error ? (
          <div className="flex flex-col items-center gap-6 py-12 text-center">
            <p className="text-red-400">{error}</p>
            <button
              type="button"
              onClick={() => callScoringAPI(profile)}
              className="rounded-lg bg-emerald-500 px-6 py-3 font-semibold text-white transition-colors hover:bg-emerald-600"
            >
              Retry
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-4 py-16">
            <div className="h-10 w-10 animate-pulse rounded-full bg-emerald-500" />
            <p className="text-sm sm:text-lg font-medium text-emerald-400 animate-pulse">
              Finding your best options&hellip;
            </p>
          </div>
        )}
      </Shell>
    );
  }

  /* ---- Normal question flow -------------------------------------- */

  return (
    <Shell progress={(step + 1) / totalSteps}>
      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{ duration: 0.25, ease: 'easeInOut' }}
        >
          {renderQuestion()}
        </motion.div>
      </AnimatePresence>
    </Shell>
  );
}

/* ------------------------------------------------------------------ */
/*  Small internal helpers                                             */
/* ------------------------------------------------------------------ */

function Shell({
  children,
  progress,
}: {
  children: React.ReactNode;
  progress: number;
}) {
  return (
    <div className="mx-auto w-full max-w-md rounded-2xl bg-slate-950 p-4 sm:p-6">
      {/* progress bar */}
      <div className="mb-5 sm:mb-8 h-1.5 w-full overflow-hidden rounded-full bg-slate-800">
        <div
          className="h-full rounded-full bg-emerald-500 transition-all duration-300"
          style={{ width: `${Math.round(progress * 100)}%` }}
        />
      </div>
      {children}
    </div>
  );
}

function QuestionWrapper({
  title,
  children,
  onBack,
}: {
  title: string;
  children: React.ReactNode;
  onBack?: () => void;
}) {
  return (
    <div className="flex flex-col gap-5">
      <h2 className="text-base sm:text-xl font-semibold text-white">{title}</h2>
      {children}
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="w-full text-center text-sm text-slate-500 hover:text-slate-300 transition-colors py-1"
        >
          &larr; Back
        </button>
      )}
    </div>
  );
}
